/**
 * .MD Vault / Vault Notes shared types.
 *
 * The Vault is the written-memory layer of the Command Center: long Markdown
 * working docs, SOPs, client docs, prompt/workflow/agent specs, meeting/call
 * notes, decision logs, and templates. Stored in Supabase/Postgres (source of
 * truth); Airtable records are link targets, not storage.
 *
 * These types are the single source of truth for both the Worker
 * (apps/worker/src/lib/vault.ts) and the Admin UI (apps/web). Keep the const
 * arrays and the union types in sync — the unions are derived from the arrays.
 */

export const VAULT_DOC_TYPES = [
  'working_doc',
  'sop',
  'internal_doc',
  'client_doc',
  'meeting_notes',
  'proposal_draft',
  'prompt_spec',
  'workflow_spec',
  'agent_spec',
  'decision_log',
  'template',
  'policy',
] as const;
export type VaultDocType = (typeof VAULT_DOC_TYPES)[number];

export const VAULT_STATUSES = ['draft', 'review', 'published', 'archived'] as const;
export type VaultStatus = (typeof VAULT_STATUSES)[number];

export const VAULT_VISIBILITIES = [
  'internal',
  'private',
  'global_client',
  'client_scoped',
] as const;
export type VaultVisibility = (typeof VAULT_VISIBILITIES)[number];

/** Defaults applied to every new document. Nothing is client-visible by accident. */
export const VAULT_DEFAULT_DOC_TYPE: VaultDocType = 'working_doc';
export const VAULT_DEFAULT_STATUS: VaultStatus = 'draft';
export const VAULT_DEFAULT_VISIBILITY: VaultVisibility = 'internal';

/** Entity types a document can be linked to. Airtable + Supabase records. */
export const VAULT_LINK_ENTITY_TYPES = [
  'organization',
  'contact',
  'user',
  'task',
  'project',
  'proposal',
  'lead',
  'website',
  'agent',
  'prompt',
  'workflow',
  'invoice',
  'call',
  'form',
  'form_entry',
] as const;
export type VaultLinkEntityType = (typeof VAULT_LINK_ENTITY_TYPES)[number];

/** Suggested relation types. Stored as free text so this stays future-proof. */
export const VAULT_RELATION_TYPES = [
  'about',
  'source',
  'output',
  'meeting_notes',
  'sop_for',
  'client_visible',
  'internal_reference',
] as const;
export type VaultRelationType = (typeof VAULT_RELATION_TYPES)[number];

export interface VaultDocument {
  id: string;
  title: string;
  slug: string;
  docType: VaultDocType;
  status: VaultStatus;
  visibility: VaultVisibility;
  contentMd: string;
  summary: string | null;
  folderPath: string | null;
  tags: string[];
  ownerUserId: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  /** Client tenant ids this doc is scoped to (matched against actor.clientIds). */
  clientIds: string[];
  /** Reserved: the source doc a record was extracted from, for future backlinks. */
  sourceDocumentId: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  archivedAt: string | null;
}

export interface VaultDocumentLink {
  id: string;
  documentId: string;
  entityType: VaultLinkEntityType;
  entityId: string;
  /** Human-readable name of the linked record, captured at link time. */
  entityLabel: string | null;
  relationType: string;
  clientId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface VaultDocumentVersion {
  id: string;
  documentId: string;
  version: number;
  title: string | null;
  contentMd: string | null;
  summary: string | null;
  docType: string | null;
  status: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Detail response: a document plus its linked records. */
export interface VaultDocumentDetail {
  document: VaultDocument;
  links: VaultDocumentLink[];
  latestVersion?: VaultDocumentVersion | null;
}

export interface VaultDocumentInput {
  title: string;
  docType?: VaultDocType;
  status?: VaultStatus;
  visibility?: VaultVisibility;
  contentMd?: string;
  summary?: string | null;
  folderPath?: string | null;
  tags?: string[];
  clientIds?: string[];
  sourceDocumentId?: string | null;
}

export type VaultDocumentPatch = Partial<VaultDocumentInput>;

export interface VaultLinkInput {
  entityType: VaultLinkEntityType;
  entityId: string;
  entityLabel?: string | null;
  relationType?: string;
  clientId?: string | null;
}

export interface VaultListParams {
  q?: string;
  docType?: VaultDocType;
  status?: VaultStatus;
  visibility?: VaultVisibility;
  folderPath?: string;
  limit?: number;
}

export interface VaultListResponse {
  items: VaultDocument[];
  total: number;
}

export interface VaultDocumentUpsertRequest {
  tenant_id: string;
  slug: string;
  title: string;
  doc_type: VaultDocType;
  status: VaultStatus;
  visibility: VaultVisibility;
  folder_path: string | null;
  tags: string[];
  content_md: string;
  summary?: string | null;
  user_id?: string;
  client_id?: string | null;
  create_version?: boolean;
}

export interface VaultDocumentUpsertResponse {
  document_id: string;
  slug: string;
  version: number | null;
  updated_at: string;
}

export interface VaultDocumentSearchParams {
  q?: string;
  tenantId?: string;
  docType?: VaultDocType;
  status?: VaultStatus;
  visibility?: VaultVisibility;
  clientId?: string;
  limit?: number;
}

export interface VaultDocumentSearchResult {
  document_id: string;
  slug: string;
  title: string;
  doc_type: VaultDocType;
  status: VaultStatus;
  visibility: VaultVisibility;
  folder_path: string | null;
  tags: string[];
  snippet: string;
  updated_at: string;
}

export interface VaultDocumentSearchResponse {
  items: VaultDocumentSearchResult[];
  total: number;
}
